import { useMemo, useState } from 'react';
import { ALL_PROJECTS, useNow, useRigg } from '@/data/RiggProvider.tsx';
import { prodDeploy, queueCounts, type Queue } from '@/data/selectors.ts';
import { api } from '@/lib/api.ts';
import { ago, cn, initial } from '@/lib/format.ts';
import { navigate, type Route } from '@/lib/router.ts';
import { Button, Dialog, Field, Input, Select, Tip, useToast } from './ui.tsx';
import { DeployDialog } from './DeployDialog.tsx';
import { suggestedFollowUps } from './FollowUps.tsx';

const QUEUES: { key: Queue; label: string; badge: 'accent' | 'warn' | 'plain' }[] = [
  { key: 'tasks', label: 'Tasks', badge: 'plain' },
  { key: 'needs-you', label: 'Needs you', badge: 'accent' },
  { key: 'approval', label: 'Awaiting approval', badge: 'plain' },
  { key: 'failed', label: 'Failed', badge: 'warn' },
  { key: 'merging', label: 'Merging', badge: 'plain' },
  { key: 'merged', label: 'Merged', badge: 'plain' },
  { key: 'deployed', label: 'Deployed', badge: 'plain' },
  { key: 'snoozed', label: 'Snoozed', badge: 'plain' },
  { key: 'trash', label: 'Trash', badge: 'plain' },
];

/**
 * `projectOnly` pages manage or describe a single project — a repository
 * list, a settings form, a member roster — and none of them has an answer
 * when there isn't one project to be about. They disappear from the menu
 * the moment "All projects" is selected rather than rendering broken; the
 * agent protocol is the one page here that was never about a project in the
 * first place, so it stays.
 */
const PAGES: { path: Route['name']; label: string; projectOnly?: boolean }[] = [
  { path: 'overview', label: 'Overview', projectOnly: true },
  { path: 'repos', label: 'Repositories', projectOnly: true },
  { path: 'intakes', label: 'Intakes', projectOnly: true },
  { path: 'agents', label: 'Agents', projectOnly: true },
  { path: 'people', label: 'People', projectOnly: true },
  { path: 'protocol', label: 'Agent protocol' },
  { path: 'api-docs', label: 'API docs' },
  { path: 'settings', label: 'Settings', projectOnly: true },
];

export function Sidebar({
  route,
  queue,
  onQueue,
  onNavigate,
}: {
  route: Route;
  queue: Queue;
  onQueue: (queue: Queue) => void;
  /** Called whenever something in here takes you somewhere — closes the mobile drawer. */
  onNavigate?: () => void;
}) {
  const {
    data,
    projects,
    project,
    setProject,
    allProjects,
    me,
    role,
    isAdminOfProject,
    isAdminOfTask,
    signOut,
    refresh,
  } = useRigg();
  const now = useNow();
  // The banner below is single-project — see `allProjects` further down —
  // so there is no ambiguity about which project's workflow name it wants.
  const prodWorkflow = project ? data.settings[project.id]?.prod_workflow : undefined;
  const prod = prodDeploy(data.ciRuns, prodWorkflow);
  const [deploying, setDeploying] = useState(false);
  const { notify } = useToast();
  const [newProject, setNewProject] = useState(false);
  const [name, setName] = useState('');
  const [repo, setRepo] = useState('');

  // What the App can already see. Requiring one at creation is the point: a
  // project without a repository looks like it works right up until its first
  // task reports a pull request nobody can post to.
  const candidates = useMemo(
    () =>
      data.installationRepos
        .map((r) => `${r.owner}/${r.name}`)
        .sort((a, b) => a.localeCompare(b)),
    [data.installationRepos],
  );

  const counts = useMemo(() => queueCounts(data, isAdminOfTask, now), [data, isAdminOfTask, now]);
  const suggestedCount = useMemo(() => suggestedFollowUps(data.followUps).length, [data.followUps]);
  const agentCount = Object.keys(data.agents).length;
  const pages = allProjects ? PAGES.filter((page) => !page.projectOnly) : PAGES;

  return (
    <nav className="flex h-full min-h-0 flex-col overflow-y-auto border-r border-line bg-side px-3.5 pt-4.5 pb-4">
      <div className="mb-4 flex items-center gap-2.5 pb-1">
        <button
          onClick={() => {
            navigate('/');
            onNavigate?.();
          }}
          className="rg-focus flex min-w-0 flex-1 items-center gap-2.5 rounded px-1.5 text-left"
        >
          <span className="size-[22px] shrink-0 rounded-md bg-accent" aria-hidden />
          <span className="text-[15.5px] font-bold tracking-[-0.01em] text-hi">Rigg</span>
        </button>
        {/* Only the drawer needs a way to close itself without picking anything — at `lg` the sidebar never closes. */}
        <button
          onClick={() => onNavigate?.()}
          aria-label="Close menu"
          className="rg-focus grid size-8 shrink-0 place-items-center rounded-full text-soft hover:bg-btn hover:text-hi2 lg:hidden"
        >
          ✕
        </button>
      </div>

      <div className="px-1.5 pb-1.5 text-[11.5px] font-semibold text-mute">Project</div>
      <div className="mb-4 flex gap-1.5">
        <Select
          value={project?.id ?? (allProjects ? ALL_PROJECTS : '')}
          onChange={(id) => {
            if (id === '__new') setNewProject(true);
            else {
              setProject(id);
              onNavigate?.();
            }
          }}
          options={[
            // Offered even with only one project — dropping it once a second
            // project is removed would otherwise strand whoever already had
            // it selected with a value the list no longer has an entry for.
            ...(projects.length > 0 ? [{ value: ALL_PROJECTS, label: 'All projects' }] : []),
            ...projects.map((p) => ({ value: p.id, label: p.name })),
            { value: '__new', label: '+ New project' },
          ]}
          placeholder="Choose a project"
          aria-label="Project"
          className="min-w-0 flex-1"
        />
      </div>

      {/*
        Production, where you cannot miss it. It is the one piece of state that
        belongs to the project rather than to any task, and the question it
        answers — is what we merged actually out? — is otherwise answerable only
        by opening the Actions tab.
      */}
      {prodWorkflow ? (
        <div className="mb-2 flex items-stretch gap-1.5">
          {prod.state !== 'none' ? (
            <a
              href={prod.url ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="flex min-w-0 flex-1 items-center gap-2 rounded-[9px] border border-line px-3 py-2 no-underline hover:bg-navhover"
            >
              <span
                className={cn(
                  'size-2 shrink-0 rounded-full',
                  prod.state === 'passing' ? 'bg-goodx' : prod.state === 'failing' ? 'bg-warnx' : 'bg-amberx',
                  prod.state === 'running' && 'animate-pulse',
                )}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-t7">
                Production <span className="text-mute">{prod.word}</span>
              </span>
              {prod.at ? <span className="shrink-0 text-[11.5px] text-mute">{ago(prod.at, now)}</span> : null}
            </a>
          ) : (
            <span className="flex flex-1 items-center px-3 text-[12.5px] text-t7">Production</span>
          )}
          <Button size="sm" className="shrink-0" onClick={() => setDeploying(true)}>
            Deploy
          </Button>
          <DeployDialog
            key={project!.id}
            projectId={project!.id}
            onNavigate={onNavigate}
            open={deploying}
            onOpenChange={setDeploying}
            canDeploy={isAdminOfProject(project!.id)}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-0.5">
        {QUEUES.map((item) => {
          const count = counts[item.key];
          // Empty tail queues are noise; the ones that matter are always shown.
          if (
            count === 0 &&
            (item.key === 'failed' || item.key === 'merging' || item.key === 'snoozed' || item.key === 'trash')
          ) {
            return null;
          }
          const active = route.name === 'tasks' && queue === item.key;
          return (
            <button
              key={item.key}
              onClick={() => {
                onQueue(item.key);
                if (route.name !== 'tasks') navigate('/');
                onNavigate?.();
              }}
              className={cn(
                'rg-focus flex w-full cursor-pointer items-center justify-between rounded-[9px] px-3 py-2 text-left text-[13.5px] font-semibold',
                active ? 'bg-active text-hi3' : 'text-t7 hover:bg-navhover hover:text-hi3',
              )}
            >
              <span>{item.label}</span>
              <Badge kind={item.badge} count={count} active={active} />
            </button>
          );
        })}
        {/* Not a queue: a suggestion is not a task yet, so it gets a page of its own. */}
        <button
          onClick={() => {
            navigate('/suggested');
            onNavigate?.();
          }}
          className={cn(
            'rg-focus flex w-full cursor-pointer items-center justify-between rounded-[9px] px-3 py-2 text-left text-[13.5px] font-semibold',
            route.name === 'suggested' ? 'bg-active text-hi3' : 'text-t7 hover:bg-navhover hover:text-hi3',
          )}
        >
          <span>Suggested</span>
          <Badge kind="plain" count={suggestedCount} active={route.name === 'suggested'} />
        </button>
      </div>

      <div className="mx-1.5 my-3.5 h-px bg-line" />

      <div className="flex flex-col gap-0.5">
        {pages.map((page) => (
          <button
            key={page.path}
            onClick={() => {
              navigate(`/${page.path}`);
              onNavigate?.();
            }}
            className={cn(
              'rg-focus flex w-full cursor-pointer items-center justify-between rounded-[9px] px-3 py-2 text-left text-[13.5px] font-semibold',
              route.name === page.path ? 'bg-active text-hi3' : 'text-t7 hover:bg-navhover hover:text-hi3',
            )}
          >
            <span>{page.label}</span>
            {page.path === 'agents' && agentCount > 0 ? (
              <span className="text-[12px] font-semibold text-mute">{agentCount}</span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="flex-1" />

      <div className="border-t border-line pt-3.5">
        <div className="flex items-center gap-2.5 px-1.5">
          <span className="grid size-[26px] shrink-0 place-items-center rounded-full bg-avatar text-[11px] font-[650] text-t6">
            {initial(me?.name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium text-t1">{me?.name}</div>
            <div className="truncate text-[11.5px] text-mute">{me?.email}</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 px-1.5">
          <Tip
            content={
              data.live
                ? 'Live — changes arrive as they happen'
                : 'Reconnecting… the board may be a few seconds behind'
            }
          >
            <span
              className={cn(
                'size-1.5 shrink-0 rounded-full',
                data.live ? 'bg-accent' : 'bg-amberx',
                data.live && 'rg-live',
              )}
            />
          </Tip>
          <span className="text-[11.5px] font-semibold text-mute capitalize">
            {/* "All projects" has no single role — admin if any of them says so,
                the same standard the sidebar's own counts use per task. */}
            {allProjects ? (projects.some((p) => isAdminOfProject(p.id)) ? 'admin' : 'member') : role}
          </span>
          <span className="flex-1" />
          {/* Padded past its text so the tap target clears ~32px without
              pushing the row itself any taller — the negative margin gives
              the extra hit area back to the layout around it. */}
          <button
            onClick={() => void signOut()}
            className="rg-focus -my-2 cursor-pointer rounded px-1 py-2 text-[11.5px] font-semibold text-mute hover:text-hi2"
          >
            Sign out
          </button>
        </div>
      </div>

      <Dialog
        open={newProject}
        onOpenChange={setNewProject}
        title="New project"
        description="A project holds its own repositories, routines and categories. Round-robin runs inside one."
        footer={
          <>
            <Button onClick={() => setNewProject(false)}>Cancel</Button>
            <Button
              tone="primary"
              disabled={!name.trim() || !repo}
              onClick={async () => {
                try {
                  const result = await api.createProject(name.trim(), repo);
                  setNewProject(false);
                  setName('');
                  setRepo('');
                  refresh();
                  setProject(result.project.id);
                } catch (error) {
                  notify('Could not create the project', {
                    body: (error as Error).message,
                    tone: 'bad',
                  });
                }
              }}
            >
              Create
            </Button>
          </>
        }
      >
        <Field
          label="Repository"
          hint={
            candidates.length
              ? 'Where this project’s work happens. Required — commenting, checks and deploys all reach GitHub through it.'
              : 'No repositories yet. Install the GitHub App on one first, under Settings.'
          }
        >
          <Select
            value={repo}
            onChange={setRepo}
            options={[
              { value: '', label: candidates.length ? 'Pick a repository' : 'None available' },
              ...candidates.map((full) => ({ value: full, label: full })),
            ]}
          />
        </Field>
        <Field label="Name">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Norad site"
            autoFocus
          />
        </Field>
      </Dialog>
    </nav>
  );
}

function Badge({
  kind,
  count,
  active,
}: {
  kind: 'accent' | 'warn' | 'plain';
  count: number;
  active: boolean;
}) {
  if (count === 0) return <span className="text-[12px] font-semibold text-mute">0</span>;
  if (kind === 'accent') {
    return (
      <span className="min-w-3 rounded-full bg-accent px-2 py-px text-center text-[11.5px] font-bold text-ink">
        {count}
      </span>
    );
  }
  if (kind === 'warn') {
    return (
      <span className="min-w-3 rounded-full bg-[rgba(233,105,90,0.9)] px-2 py-px text-center text-[11.5px] font-bold text-white">
        {count}
      </span>
    );
  }
  return (
    <span className={cn('text-[12px] font-semibold', active ? 'text-body' : 'text-mute')}>
      {count}
    </span>
  );
}
